import { Navigate, generatePath, useLocation, useParams } from 'react-router-dom';

/**
 * Pre-surfaces paths (JSS#1154) and where they live now. Bookmarks, emails
 * (boot-service approval links point at /deployments/:siteId) and older
 * auth `returnTo` states keep working through these. `:params` in `from`
 * carry over into `to` by name.
 */
export const LEGACY_REDIRECTS: readonly { from: string; to: string }[] = [
  { from: '/dashboard', to: '/home' },
  { from: '/sources', to: '/streaming/sources' },
  { from: '/sources/:sourceId', to: '/streaming/sources/:sourceId' },
  { from: '/channels', to: '/streaming/channels' },
  { from: '/channels/:channelId', to: '/streaming/channels/:channelId' },
  { from: '/vod', to: '/streaming/vod' },
  { from: '/ads', to: '/streaming/ads' },
  { from: '/drm', to: '/streaming/drm' },
  { from: '/player', to: '/streaming/player' },
  { from: '/analytics', to: '/streaming/analytics' },
  { from: '/social', to: '/media/social' },
  { from: '/processing', to: '/media/recordings' },
  { from: '/processing/recordings', to: '/media/recordings' },
  { from: '/processing/recordings/:sourceId', to: '/media/recordings/:sourceId' },
  { from: '/processing/jobs', to: '/media/jobs' },
  { from: '/processing/clips', to: '/media/clips' },
  { from: '/processing/settings', to: '/media/settings' },
  { from: '/events', to: '/audience/events' },
  { from: '/events/:eventId', to: '/audience/events/:eventId' },
  { from: '/deployments', to: '/sites' },
  { from: '/deployments/machines', to: '/sites/machines' },
  { from: '/deployments/:siteId', to: '/sites/:siteId' },
  { from: '/account', to: '/org/account' },
];

/**
 * Redirect to `to`, filling its `:params` from the matched route and
 * keeping the query string, hash and history state.
 */
export default function LegacyRedirect({ to }: { to: string }) {
  const params = useParams();
  const { search, hash, state } = useLocation();
  // The router hands params over decoded and generatePath does not encode,
  // so re-encode each one (a recording's sourceId may hold reserved chars).
  const encoded = Object.fromEntries(
    Object.entries(params).map(([k, v]) => [k, v === undefined ? v : encodeURIComponent(v)]),
  );
  const pathname = generatePath(to, encoded);
  return <Navigate to={{ pathname, search, hash }} state={state} replace />;
}
